import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
  limit,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { SerializedItem } from '@/types/schemas';

function tsToMs(v: unknown): number | undefined {
  if (!v) return undefined;
  if (typeof v === 'number') return v;
  if (typeof v === 'object' && v !== null && 'toMillis' in v) {
    return (v as { toMillis: () => number }).toMillis();
  }
  return undefined;
}

function mapSerial(id: string, data: Record<string, unknown>): SerializedItem {
  return {
    id,
    itemId: data.itemId as string,
    storeId: data.storeId as string,
    serialNumber: data.serialNumber as string,
    isAvailable: (data.isAvailable as boolean) ?? true,
    createdAt: tsToMs(data.createdAt),
    updatedAt: tsToMs(data.updatedAt),
  };
}

export async function fetchSerializedItems(
  itemId: string,
  storeId?: string,
): Promise<SerializedItem[]> {
  let q = query(collection(db, 'serialized_items'), where('itemId', '==', itemId), limit(500));
  if (storeId) {
    q = query(
      collection(db, 'serialized_items'),
      where('itemId', '==', itemId),
      where('storeId', '==', storeId),
      limit(500),
    );
  }
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapSerial(d.id, d.data() as Record<string, unknown>))
    .sort((a, b) => a.serialNumber.localeCompare(b.serialNumber));
}

export async function fetchAvailableSerializedItems(
  itemId: string,
  storeId: string,
): Promise<SerializedItem[]> {
  const snap = await getDocs(
    query(
      collection(db, 'serialized_items'),
      where('itemId', '==', itemId),
      where('storeId', '==', storeId),
      where('isAvailable', '==', true),
      limit(500),
    ),
  );
  return snap.docs.map((d) => mapSerial(d.id, d.data() as Record<string, unknown>));
}

export async function bulkCreateSerializedItems(
  items: Array<{ itemId: string; storeId: string; serialNumber: string }>,
): Promise<void> {
  const batch = writeBatch(db);
  items.forEach((item) => {
    const ref = doc(collection(db, 'serialized_items'));
    batch.set(ref, {
      ...item,
      isAvailable: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
}

export async function bulkSetSerializedUnavailable(ids: string[]): Promise<void> {
  const batch = writeBatch(db);
  ids.forEach((id) => {
    batch.update(doc(db, 'serialized_items', id), {
      isAvailable: false,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
}

export async function deleteSerializedItem(id: string): Promise<void> {
  await deleteDoc(doc(db, 'serialized_items', id));
}

export function generateSerialNumbers(
  sku: string,
  existing: string[],
  count: number,
): string[] {
  let maxIndex = 0;
  for (const s of existing) {
    const m = s.match(/-(\d+)$/);
    if (m?.[1]) maxIndex = Math.max(maxIndex, parseInt(m[1], 10));
  }
  const out: string[] = [];
  for (let i = 1; i <= count; i++) out.push(`${sku}-${maxIndex + i}`);
  return out;
}
